import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { ProductSectionSettingsService } from '../../api/services/productSectionSettingsService';
import { IProductSection } from '../../types/api/response/productSectionSettingsResponse';
import { CUSTOMER_APP_ROUTE } from '../../constants/routes';

const NAME_REGEX = /^[a-z][a-z-]*$/;

export const ProductSectionSettingsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sections, setSections] = useState<IProductSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingName, setTogglingName] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingName, setSavingName] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createNameError, setCreateNameError] = useState('');
  const [creating, setCreating] = useState(false);

  const loadSections = async () => {
    setLoading(true);
    try {
      const data = await ProductSectionSettingsService.getAllSections();
      setSections(data);
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to load sections. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (section: IProductSection) => {
    setTogglingName(section.name);
    try {
      const updated = await ProductSectionSettingsService.toggleEnable(section.name);
      setSections((prev) => prev.map((s) => (s.name === section.name ? updated : s)));
      dispatch(
        showNotification({
          message: `${section.name} ${updated.enabled ? 'enabled' : 'disabled'}.`,
          severity: 'success',
        }),
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to toggle section. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setTogglingName(null);
    }
  };

  const handleEditStart = (section: IProductSection) => {
    setEditingName(section.name);
    setEditingValue(section.display_name ?? '');
  };

  const handleEditCancel = () => {
    setEditingName(null);
    setEditingValue('');
  };

  const handleEditSave = async (sectionName: string) => {
    setSavingName(sectionName);
    try {
      const updated = await ProductSectionSettingsService.updateDisplayName(sectionName, {
        display_name: editingValue,
      });
      setSections((prev) => prev.map((s) => (s.name === sectionName ? updated : s)));
      setEditingName(null);
      dispatch(showNotification({ message: `Display name updated.`, severity: 'success' }));
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to update display name. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setSavingName(null);
    }
  };

  const handleCreateOpen = () => {
    setCreateName('');
    setCreateDisplayName('');
    setCreateNameError('');
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
  };

  const handleCreateSubmit = async () => {
    if (!createName) {
      setCreateNameError('Name is required');
      return;
    }
    if (!NAME_REGEX.test(createName)) {
      setCreateNameError('Only lowercase letters and dashes, must start with a letter');
      return;
    }
    setCreating(true);
    try {
      const created = await ProductSectionSettingsService.createSection({
        name: createName,
        display_name: createDisplayName || undefined,
      });
      setSections((prev) => [...prev, created]);
      setCreateOpen(false);
      dispatch(
        showNotification({ message: `Section "${createName}" created.`, severity: 'success' }),
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to create section. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Product Section Settings
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}>
          Add Section
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Section Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Display Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Enabled</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sections.map((section) => (
                    <TableRow key={section.name} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => navigate(`${CUSTOMER_APP_ROUTE}/${section.name}`)}
                        >
                          {section.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {editingName === section.name ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <TextField
                              size="small"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleEditSave(section.name);
                                if (e.key === 'Escape') handleEditCancel();
                                e.stopPropagation();
                              }}
                              autoFocus
                              sx={{ width: 220 }}
                            />
                            {savingName === section.name ? (
                              <CircularProgress size={20} />
                            ) : (
                              <>
                                <Tooltip title="Save">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleEditSave(section.name)}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton size="small" onClick={handleEditCancel}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">
                              {section.display_name || (
                                <Typography component="span" variant="body2" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </Typography>
                            <Tooltip title="Edit display name">
                              <IconButton size="small" onClick={() => handleEditStart(section)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {togglingName === section.name ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Switch
                            checked={section.enabled}
                            onChange={() => handleToggle(section)}
                            color="success"
                            size="small"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={handleCreateClose} maxWidth="xs" fullWidth>
        <DialogTitle>Add Section</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            size="small"
            value={createName}
            onChange={(e) => {
              setCreateName(e.target.value);
              setCreateNameError('');
            }}
            error={!!createNameError}
            sx={{ mt: 1, mb: 0.5 }}
            placeholder="e.g. seasonal-picks"
            autoFocus
          />
          {createNameError && (
            <FormHelperText error sx={{ mb: 1 }}>
              {createNameError}
            </FormHelperText>
          )}
          <FormHelperText sx={{ mb: 2 }}>
            Lowercase letters and dashes only (e.g. flash-deals)
          </FormHelperText>
          <TextField
            label="Display Name (optional)"
            fullWidth
            size="small"
            value={createDisplayName}
            onChange={(e) => setCreateDisplayName(e.target.value)}
            placeholder="e.g. Seasonal Picks"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={creating}>
            {creating ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
